import React, { useState } from 'react';
import styled from 'styled-components';
import { API_ENDPOINTS } from '../../api/config';
import ElevatedButton from '../Layout/ElevatedButton';

const Form = styled.form`
  max-width: 400px;
  margin: 2rem auto;
  padding: 2rem;
  border: 1px solid ${props => props.theme.buttonPrimary};
  border-radius: 8px;
  background: ${props => props.theme.cardBackground};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  color: ${props => props.theme.text};
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1.25rem;
  margin: 0.5rem 0;
  border: 2px solid ${props => props.theme.buttonPrimary};
  border-radius: 4px;
  font-size: 1rem;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  transition: all 0.3s ease;
  box-sizing: border-box;

  &::placeholder {
    color: ${props => props.theme.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimaryHover};
    background: ${props => props.theme.inputBackgroundFocus};
    box-shadow: 0 0 8px rgba(88, 101, 242, 0.3);
  }
`;

const ChangePasswordModal = ({ onSuccess, onCancel }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate current password is provided
    if (!currentPassword.trim()) {
      setError('Current password is required');
      setLoading(false);
      return;
    }

    // Validate passwords
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      setError('New password must contain uppercase, lowercase, and number');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.CHANGE_PASSWORD, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        onSuccess('Password changed successfully!');
      } else {
        setError(data.error || 'Password change failed');
      }
    } catch (error) {
      console.error('Password change error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <h2 style={{ color: 'var(--color-text)', textAlign: 'center', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
        Change Password
      </h2>

      <p style={{ textAlign: 'center', color: '#b9bbbe', marginBottom: '1rem' }}>
        Enter your current password and choose a new one
      </p>

      {error && (
        <div style={{
          padding: '0.75rem',
          marginBottom: '1rem',
          background: '#dc3545',
          color: 'white',
          borderRadius: '4px',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}

      <Input
        type="password"
        placeholder="Current Password"
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
        required
        disabled={loading}
        autoFocus
      />

      <Input
        type="password"
        placeholder="New Password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        required
        disabled={loading}
      />

      <Input
        type="password"
        placeholder="Confirm New Password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        required
        disabled={loading}
      />

      <ElevatedButton type="submit" style={{ width: '100%', marginTop: '1rem' }} disabled={loading}>
        {loading ? 'Changing...' : 'Change Password'}
      </ElevatedButton>

      <ElevatedButton
        type="button"
        onClick={onCancel}
        style={{
          width: '100%',
          marginTop: '0.5rem',
          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
          border: '2px solid #ff4757'
        }}
      >
        Cancel
      </ElevatedButton>
    </Form>
  );
};

export default ChangePasswordModal;